import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { MapPinned, Plus, Compass, Trash2, X, SprayCan } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { sprayLinkFor } from '@/utils/nurserySprayTargets';
import { cn } from '@/utils/cn';
import nurseryService from '../../services/nursery';
import orchardService from '../../services/orchard';
import type {
  NurseryLocation, NurseryLot, NurseryStockItem, OriginCorner, NurseryLocationType, RowSide,
} from '../../types/nursery';
import PlaceLotPanel from '../../components/nursery/PlaceLotPanel';
import { toNum } from '@/utils/numeric';
import {
  compareBlocks, countRowSlots, parseSequences, rowLabel, SIDES_FOR, stockItemLabel, type SideChoice,
} from '@/utils/nurseryRowSlots';

interface Opt { id: number; name: string; farm_id?: number; sort_order?: number | null }

/** Labels are `origin.<corner>` in the translation file. */
const ORIGINS: OriginCorner[] = ['bottom_left', 'bottom_right', 'top_left', 'top_right'];

/** Labels are `sideChoice.<choice>` in the translation file. */
const SIDE_CHOICES: SideChoice[] = ['none', 'left', 'right', 'both'];

const trees = (n: number) => n.toLocaleString('en-AU');
const totalAt = (lots: NurseryLot[]) => lots.reduce((n, l) => n + l.current_count, 0);

/**
 * What stands at each filled place, one line per lot: the place, the tree,
 * the count and its stage. Shown under the chips rather than in a tooltip,
 * which a phone in the rows can never open.
 */
function LocationContents({ places, inRow = true }: { places: { label: string; lots: NurseryLot[] }[]; inRow?: boolean }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'NurseryLocations' });
  const filled = places.filter(p => p.lots.length > 0);
  if (!filled.length) return null;
  return (
    <ul className={cn('mt-1.5 space-y-0.5 text-xs text-neutral-600', inRow && 'ml-1 sm:ml-[8.5rem]')}>
      {filled.flatMap(p => p.lots.map(l => (
        <li key={l.id} className="break-words">
          <span className={cn('font-semibold text-neutral-800', inRow && 'font-mono')}>{p.label}</span>{' '}
          <Link to={`/nursery/lots/${l.id}`} className="text-primary-700 hover:underline">{stockItemLabel(l)}</Link>
          {l.lot_code && <span className="text-neutral-400"> · {l.lot_code}</span>}
          {' '}— <span className="font-semibold text-neutral-800">{trees(l.current_count)}</span> {t(`stages.${l.current_stage}`, { defaultValue: l.current_stage })}
        </li>
      )))}
    </ul>
  );
}

/** The server refuses more than this in one request. */
const MAX_ROW_SLOTS = 5000;
const MAX_POSITIONS = 500;

// Heel-in trenches and coolstores hold bare-root trees between digging and despatch.
const SIMPLE_TYPES: NurseryLocationType[] = [
  'seed_bed', 'polyhouse', 'container_area', 'field', 'heel_in', 'coolstore', 'quarantine_area', 'shop_floor', 'storeroom', 'other',
];

function NurseryLocations() {
  const { t } = useTranslation('nursery', { keyPrefix: 'NurseryLocations' });
  const navigate = useNavigate();
  // Planning a spray needs a spray creator (or admin) seat.
  const { hasAccess: canPlanSpray } = useModuleLicense('spray', ['creator', 'admin']);
  const [farms, setFarms] = useState<Opt[]>([]);
  const [blocks, setBlocks] = useState<Opt[]>([]);
  const [farmId, setFarmId] = useState<number | ''>('');
  const [blockId, setBlockId] = useState<number | ''>('');

  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [error, setError] = useState<string | null>(null);

  // layout form
  const [heading, setHeading] = useState<number | ''>('');
  const [origin, setOrigin] = useState<OriginCorner>('bottom_left');
  const [savingLayout, setSavingLayout] = useState(false);

  // row slots form
  const [startRow, setStartRow] = useState<number | ''>('');
  const [endRow, setEndRow] = useState<number | ''>('');
  const [side, setSide] = useState<SideChoice>('none');
  const [seqStr, setSeqStr] = useState('10,20,30');
  const [addingRow, setAddingRow] = useState(false);
  const [addedNote, setAddedNote] = useState<string | null>(null);

  // deleting rows: tick them, then confirm
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // one position, from the × on its chip; confirmed under its own row
  const [pendingSlot, setPendingSlot] = useState<NurseryLocation | null>(null);

  // placing stock: click a location, choose a stock item
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [lots, setLots] = useState<NurseryLot[]>([]);
  const [placing, setPlacing] = useState<NurseryLocation | null>(null);
  const [deleting, setDeleting] = useState(false);

  // simple location form
  const [simpleName, setSimpleName] = useState('');
  const [simpleType, setSimpleType] = useState<NurseryLocationType>('seed_bed');
  const [addingSimple, setAddingSimple] = useState(false);

  useEffect(() => {
    orchardService.getFarms().then(res => {
      if (res.success) setFarms(((res.data as { farms?: Opt[] })?.farms || (res.data as unknown as Opt[]) || []));
    });
    nurseryService.getStockItems({ active: true, kind: 'plant' }).then(res => { if (res.success) setStockItems(res.data || []); });
  }, []);

  useEffect(() => {
    if (!farmId) { setBlocks([]); setBlockId(''); return; }
    orchardService.getBlocks(Number(farmId)).then(res => {
      if (res.success) setBlocks([...((res.data as { blocks?: Opt[] })?.blocks || (res.data as Opt[]) || [])].sort(compareBlocks));
    });
  }, [farmId]);

  const loadBlock = useCallback(async () => {
    if (!blockId) { setLocations([]); setLots([]); return; }
    setError(null);
    try {
      const [layoutRes, locRes, lotRes] = await Promise.all([
        nurseryService.getBlockLayout(Number(blockId)),
        nurseryService.getLocations({ block_id: Number(blockId) }),
        nurseryService.getLots({ block_id: Number(blockId), status: 'active' }),
      ]);
      if (lotRes.success) setLots(lotRes.data || []);
      if (layoutRes.success && layoutRes.data) {
        setHeading(toNum(layoutRes.data.heading_degrees) ?? '');
        setOrigin(layoutRes.data.origin_corner || 'bottom_left');
      }
      if (locRes.success) setLocations(locRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('errors.loadBlock'));
    }
  }, [blockId, t]);

  useEffect(() => { loadBlock(); }, [loadBlock]);

  const saveLayout = async () => {
    if (!blockId) return;
    setSavingLayout(true);
    try {
      await nurseryService.upsertBlockLayout(Number(blockId), {
        heading_degrees: heading === '' ? null : Number(heading),
        origin_corner: origin,
      });
      await loadBlock();
    } finally {
      setSavingLayout(false);
    }
  };

  // A blank "to row" is a single row.
  const lastRow = endRow === '' ? startRow : endRow;
  const sequences = useMemo(() => parseSequences(seqStr), [seqStr]);
  const slotCount = startRow !== '' && lastRow !== '' ? countRowSlots(startRow, lastRow, side, sequences.length) : 0;
  const rowsBackwards = startRow !== '' && lastRow !== '' && lastRow < startRow;
  const tooManyPositions = sequences.length > MAX_POSITIONS;
  const tooMany = slotCount > MAX_ROW_SLOTS || tooManyPositions;

  const addRows = async () => {
    if (!blockId || startRow === '' || lastRow === '' || !slotCount || tooMany) return;
    setAddingRow(true);
    setError(null);
    setAddedNote(null);
    try {
      const res = await nurseryService.createRowSlots({
        block_id: Number(blockId), start_row: startRow, end_row: lastRow, sides: SIDES_FOR[side], sequences,
      });
      if (res.success) {
        const added = res.data?.length ?? 0;
        const skipped = (res as { skipped?: number }).skipped ?? slotCount - added;
        setAddedNote(t('added', { count: added }) + (skipped > 0 ? t('alreadyExisted', { count: skipped }) : ''));
        setStartRow('');
        setEndRow('');
        await loadBlock();
      } else {
        setError((res as { error?: { message?: string } }).error?.message || t('errors.addRows'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('errors.addRows'));
    } finally {
      setAddingRow(false);
    }
  };

  const lotsAt = (locationId: number) => lotsByLocation.get(locationId) ?? [];

  const openPlacing = (loc: NurseryLocation) => {
    setConfirmingDelete(false);
    setPendingSlot(null);
    setAddedNote(null);
    setPlacing(loc);
  };

  const placed = async (note: string) => {
    setPlacing(null);
    setAddedNote(note);
    await loadBlock();
  };

  const toggleRow = (key: string) => {
    setConfirmingDelete(false);
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const deleteSelected = () =>
    removeSlots(rows.filter(r => selectedRows.has(r.key)).flatMap(r => r.slots.map(s => s.id)));

  const removeSlots = async (ids: number[]) => {
    if (!blockId || !ids.length) return;
    setDeleting(true);
    setError(null);
    setAddedNote(null);
    try {
      const res = await nurseryService.deleteRowSlots({ block_id: Number(blockId), ids });
      if (res.success) {
        const deleted = res.data?.deleted ?? 0;
        const kept = res.data?.kept ?? 0;
        setAddedNote(t('deleted', { count: deleted }) + (kept > 0 ? t('kept', { count: kept }) : ''));
        setSelectedRows(new Set());
        setConfirmingDelete(false);
        setPendingSlot(null);
        await loadBlock();
      } else {
        setError((res as { error?: { message?: string } }).error?.message || t('errors.deleteRows'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('errors.deleteRows'));
    } finally {
      setDeleting(false);
    }
  };

  const addSimple = async () => {
    if (!simpleName.trim()) return;
    setAddingSimple(true);
    try {
      const res = await nurseryService.createLocation({
        name: simpleName.trim(),
        location_type: simpleType,
        farm_id: farmId ? Number(farmId) : null,
        block_id: blockId ? Number(blockId) : null,
      });
      if (res.success) { setSimpleName(''); await loadBlock(); }
    } finally {
      setAddingSimple(false);
    }
  };

  // group nursery_row slots by row, and by side of the track where there is one
  const rows = useMemo(() => {
    const map = new Map<string, { key: string; rowNumber: number; side: RowSide | null; slots: NurseryLocation[] }>();
    for (const loc of locations) {
      if (loc.location_type === 'nursery_row' && loc.row_number != null) {
        const rowSide = loc.row_side ?? null;
        const key = `${loc.row_number}:${rowSide ?? ''}`;
        if (!map.has(key)) map.set(key, { key, rowNumber: loc.row_number, side: rowSide, slots: [] });
        map.get(key)!.slots.push(loc);
      }
    }
    return Array.from(map.values())
      .sort((a, b) => a.rowNumber - b.rowNumber || (a.side ?? '').localeCompare(b.side ?? ''))
      .map(r => ({ ...r, slots: r.slots.sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0)) }));
  }, [locations]);

  // what already stands where, so an occupied position shows as one
  const lotsByLocation = useMemo(() => {
    const map = new Map<number, NurseryLot[]>();
    for (const lot of lots) {
      if (lot.location_id == null || lot.current_count <= 0) continue;
      if (!map.has(lot.location_id)) map.set(lot.location_id, []);
      map.get(lot.location_id)!.push(lot);
    }
    return map;
  }, [lots]);

  const selectedSlotCount =rows.reduce((n, r) => n + (selectedRows.has(r.key) ? r.slots.length : 0), 0);

  const simpleLocations = useMemo(
    () => locations.filter(l => l.location_type !== 'nursery_row'),
    [locations],
  );

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<MapPinned className="h-6 w-6 text-white" />}
      />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <select value={farmId} onChange={e => setFarmId(e.target.value ? Number(e.target.value) : '')}
            aria-label={t('farm')} className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            <option value="">{t('selectFarm')}</option>
            {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select value={blockId} onChange={e => {
              setBlockId(e.target.value ? Number(e.target.value) : '');
              setAddedNote(null);
              setSelectedRows(new Set());
              setConfirmingDelete(false);
              setPendingSlot(null);
              setPlacing(null);
            }}
            disabled={!farmId} aria-label={t('block')} className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            <option value="">{t('selectBlock')}</option>
            {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>}

        {!blockId ? (
          <div className="text-center py-16 text-neutral-400">
            <MapPinned className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{t('emptyTitle')}</p>
            <p className="text-sm mt-1">{t('emptyHelp')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Block layout */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Compass className="h-5 w-5 text-primary-600" />
                <h3 className="text-sm font-semibold text-neutral-800">{t('blockLayout')}</h3>
              </div>
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('rowHeading')}</label>
                  <input type="number" min="0" max="360" value={heading}
                    onChange={e => setHeading(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-40 border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder={t('rowHeadingPlaceholder')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('originCorner')}</label>
                  <select value={origin} onChange={e => setOrigin(e.target.value as OriginCorner)}
                    className="border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                    {ORIGINS.map(o => <option key={o} value={o}>{t(`origin.${o}`)}</option>)}
                  </select>
                </div>
                <Button variant="primary" size="sm" onClick={saveLayout} disabled={savingLayout}>
                  {savingLayout ? t('saving') : t('saveLayout')}
                </Button>
              </div>
              <p className="text-xs text-neutral-400 mt-3">
                {t('layoutHelp', { corner: t(`origin.${origin}`).toLowerCase() })}
              </p>
            </div>

            {/* Rows + sequences */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-neutral-800 mb-4">{t('rowsAndSequences')}</h3>

              <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
                <div>
                  <label htmlFor="slots-start-row" className="block text-sm font-medium text-neutral-700 mb-1">{t('fromRow')}</label>
                  <input id="slots-start-row" type="number" min="1" value={startRow}
                    onChange={e => setStartRow(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    className="w-full sm:w-24 border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label htmlFor="slots-end-row" className="block text-sm font-medium text-neutral-700 mb-1">{t('toRow')}</label>
                  <input id="slots-end-row" type="number" min="1" value={endRow}
                    placeholder={startRow === '' ? '' : String(startRow)}
                    onChange={e => setEndRow(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    className="w-full sm:w-24 border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label htmlFor="slots-sequences" className="block text-sm font-medium text-neutral-700 mb-1">{t('positionsInRow')}</label>
                  <input id="slots-sequences" value={seqStr} onChange={e => setSeqStr(e.target.value)}
                    className="w-full sm:w-56 border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder={t('positionsPlaceholder')} />
                </div>
                <div className="col-span-2">
                  <label htmlFor="slots-side" className="block text-sm font-medium text-neutral-700 mb-1">{t('track')}</label>
                  <select id="slots-side" value={side} onChange={e => setSide(e.target.value as SideChoice)}
                    className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                    {SIDE_CHOICES.map(c => <option key={c} value={c}>{t(`sideChoice.${c}`)}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs text-neutral-400 mt-2">
                {t('rowsHelp')}
              </p>

              <div className="flex items-center gap-3 flex-wrap mt-3 mb-5">
                <Button variant="outline" size="sm" onClick={addRows} disabled={addingRow || !slotCount || tooMany}>
                  <Plus className="h-4 w-4" /> {addingRow ? t('adding') : slotCount > 1 ? t('addLocations', { n: slotCount }) : t('addRows')}
                </Button>
                {rowsBackwards ? (
                  <p className="text-sm text-red-700">{t('rowsBackwards')}</p>
                ) : tooManyPositions ? (
                  <p className="text-sm text-red-700">{t('tooManyPositions', { n: sequences.length, max: MAX_POSITIONS })}</p>
                ) : tooMany ? (
                  <p className="text-sm text-red-700">{t('tooManySlots', { n: slotCount, max: MAX_ROW_SLOTS })}</p>
                ) : slotCount > 0 ? (
                  <p className="text-sm text-neutral-600 min-w-0 break-words">
                    {startRow === lastRow ? t('preview.row', { row: startRow }) : t('preview.rows', { from: startRow, to: lastRow })}
                    {side === 'none' ? '' : t(`preview.side.${side}`)}
                    {t('preview.positions', { count: sequences.length })}
                    {sequences.length > 8
                      ? t('preview.many', { first: sequences.slice(0, 3).join(', '), last: sequences[sequences.length - 1], n: sequences.length })
                      : sequences.join(', ')}.
                  </p>
                ) : null}
              </div>

              {addedNote && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 mb-4">{addedNote}</div>
              )}

              {rows.length === 0 ? (
                <p className="text-sm text-neutral-400">{t('noRows')}</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 flex-wrap border-b border-neutral-100 pb-3 mb-1">
                    <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                      <input type="checkbox" className="h-4 w-4 rounded border-neutral-300"
                        checked={selectedRows.size === rows.length}
                        onChange={e => { setConfirmingDelete(false); setSelectedRows(e.target.checked ? new Set(rows.map(r => r.key)) : new Set()); }} />
                      {t('selectAllRows')}
                    </label>
                    {lots.length > 0 && (
                      <span className="text-sm text-neutral-600">
                        {t('treesInBlock', { n: trees(totalAt(lots.filter(l => l.location_id != null && l.current_count > 0))) })}
                      </span>
                    )}
                    {/* Nursery spraying: the ticked rows go straight into the
                        spray wizard. A spray seat that can plan sprays only. */}
                    {selectedRows.size > 0 && canPlanSpray && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(sprayLinkFor({
                          farmId: farmId ? Number(farmId) : null,
                          blockId: blockId ? Number(blockId) : null,
                          rows: rows.filter(r => selectedRows.has(r.key)).map(r => ({ row: r.rowNumber, side: r.side })),
                        }))}
                      >
                        <SprayCan className="h-4 w-4" /> {t('sprayRows', { count: selectedRows.size })}
                      </Button>
                    )}
                    {selectedRows.size > 0 && !confirmingDelete && (
                      <Button variant="danger" size="sm" onClick={() => { setPendingSlot(null); setPlacing(null); setConfirmingDelete(true); }}>
                        <Trash2 className="h-4 w-4" /> {t('deleteRows', { count: selectedRows.size })}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-neutral-400">
                    {t('clickHelp')}
                  </p>
                  {confirmingDelete && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-3 text-sm text-red-800">
                      <p className="break-words">
                        {t('confirmDelete', { rows: t('rowCount', { count: selectedRows.size }), locations: t('locationCount', { count: selectedSlotCount }) })}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mt-2">
                        <Button variant="danger" size="sm" onClick={deleteSelected} disabled={deleting}>
                          {deleting ? t('deleting') : t('delete')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)} disabled={deleting}>{t('cancel')}</Button>
                      </div>
                    </div>
                  )}
                  {rows.map(r => (
                    <div key={r.key}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 w-32 flex-shrink-0">
                          <input type="checkbox" className="h-4 w-4 rounded border-neutral-300"
                            checked={selectedRows.has(r.key)} onChange={() => toggleRow(r.key)} />
                          {rowLabel(r.rowNumber, r.side)}
                        </label>
                        {r.slots.map(s => (
                          <span key={s.id} className={cn(
                            'inline-flex items-center pr-1 rounded-full text-xs font-mono border',
                            pendingSlot?.id === s.id ? 'bg-red-50 text-red-800 border-red-200'
                              : lotsAt(s.id).length ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-primary-50 text-primary-700 border-primary-100',
                            placing?.id === s.id && 'ring-2 ring-primary-400',
                          )}>
                            <button type="button" onClick={() => openPlacing(s)}
                              aria-label={t('placeAtAria', { row: rowLabel(r.rowNumber, r.side), n: s.sequence_number })}
                              title={lotsAt(s.id).length ? lotsAt(s.id).map(l => `${stockItemLabel(l)} · ${l.current_count}`).join('\n') : t('emptyHere')}
                              className="pl-2.5 pr-1 py-1 rounded-l-full hover:underline">
                              S{s.sequence_number}
                              {lotsAt(s.id).length > 0 && <span className="font-sans font-semibold"> · {trees(totalAt(lotsAt(s.id)))}</span>}
                            </button>
                            <button type="button" onClick={() => { setConfirmingDelete(false); setPlacing(null); setPendingSlot(s); }}
                              aria-label={t('deleteAria', { row: rowLabel(r.rowNumber, r.side), n: s.sequence_number })}
                              className="ml-0.5 p-1 rounded-full hover:bg-black/10">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <LocationContents places={r.slots.map(s => ({ label: `S${s.sequence_number}`, lots: lotsAt(s.id) }))} />
                      {pendingSlot && r.slots.some(s => s.id === pendingSlot.id) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-3 text-sm text-red-800 mt-2">
                          <p className="break-words">
                            {t('confirmDeletePosition', { n: pendingSlot.sequence_number, row: rowLabel(r.rowNumber, r.side) })}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            <Button variant="danger" size="sm" onClick={() => removeSlots([pendingSlot.id])} disabled={deleting}>
                              {deleting ? t('deleting') : t('deletePosition')}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setPendingSlot(null)} disabled={deleting}>{t('cancel')}</Button>
                          </div>
                        </div>
                      )}
                      {placing && r.slots.some(s => s.id === placing.id) && (
                        <PlaceLotPanel key={placing.id} location={placing}
                          title={t('slotTitle', { row: rowLabel(r.rowNumber, r.side), n: placing.sequence_number })}
                          lotsHere={lotsAt(placing.id)} stockItems={stockItems}
                          onPlaced={placed} onCancel={() => setPlacing(null)} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Simple locations */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-neutral-800 mb-4">{t('otherLocations')}</h3>
              <div className="flex items-end gap-3 mb-4 flex-wrap">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('name')}</label>
                  <input value={simpleName} onChange={e => setSimpleName(e.target.value)}
                    className="w-full sm:w-56 border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder={t('namePlaceholder')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('type')}</label>
                  <select value={simpleType} onChange={e => setSimpleType(e.target.value as NurseryLocationType)}
                    className="border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                    {SIMPLE_TYPES.map(ty => <option key={ty} value={ty}>{t(`locationType.${ty}`)}</option>)}
                  </select>
                </div>
                <Button variant="outline" size="sm" onClick={addSimple} disabled={addingSimple || !simpleName.trim()}>
                  <Plus className="h-4 w-4" /> {t('add')}
                </Button>
              </div>
              {simpleLocations.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {simpleLocations.map(l => (
                    <button key={l.id} type="button" onClick={() => openPlacing(l)}
                      title={lotsAt(l.id).length ? lotsAt(l.id).map(x => `${stockItemLabel(x)} · ${x.current_count}`).join('\n') : t('emptyHere')}
                      className={cn(
                        'inline-flex items-center px-2.5 py-1 rounded-full text-xs text-left break-words hover:underline',
                        lotsAt(l.id).length ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-700',
                        placing?.id === l.id && 'ring-2 ring-primary-400',
                      )}>
                      {l.name} <span className="opacity-70 ml-1">· {t(`locationType.${l.location_type}`, { defaultValue: l.location_type.replace('_', ' ') })}</span>
                      {lotsAt(l.id).length > 0 && <span className="font-semibold ml-1">· {trees(totalAt(lotsAt(l.id)))}</span>}
                    </button>
                  ))}
                </div>
              )}
              <LocationContents inRow={false}
                places={simpleLocations.map(l => ({ label: l.name, lots: lotsAt(l.id) }))} />
              {placing && simpleLocations.some(l => l.id === placing.id) && (
                <PlaceLotPanel key={placing.id} location={placing} title={placing.name}
                  lotsHere={lotsAt(placing.id)} stockItems={stockItems}
                  onPlaced={placed} onCancel={() => setPlacing(null)} />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default NurseryLocations;
